import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const FlashSalesBanner: React.FC = () => {
    const { t } = useTranslation('marketplace');
    const navigate = useNavigate();
    const [timeLeft, setTimeLeft] = useState({ h: 2, m: 35, s: 12 });

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev.s > 0) return { ...prev, s: prev.s - 1 };
                if (prev.m > 0) return { ...prev, m: prev.m - 1, s: 59 };
                if (prev.h > 0) return { h: prev.h - 1, m: 59, s: 59 };
                return prev;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const format = (n: number) => n.toString().padStart(2, '0');

    return (
        <div className="px-4 py-8">
            <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                className="relative overflow-hidden rounded-[3.5rem] bg-[#121212] p-10 text-white shadow-premium-dark border border-white/5"
            >
                {/* 🌈 High-Fidelity Apple Gradient Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A1A] via-[#0D0D0D] to-[#252525] -z-20" />

                {/* 🔮 Atmospheric Blurs */}
                <div className="absolute -right-20 -top-20 w-[400px] h-[400px] bg-purple-600/20 rounded-full blur-[120px] mix-blend-screen animate-pulse-slow" />
                <div className="absolute -left-20 -bottom-20 w-[300px] h-[300px] bg-brand-terracotta/15 rounded-full blur-[100px] mix-blend-screen" />

                {/* 🎞️ The Grainy Noise Layer (User Requested) */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.15] brightness-125 contrast-125 pointer-events-none mix-blend-overlay" />

                <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
                    <div className="space-y-6 max-w-xl">
                        <div className="flex flex-wrap items-center gap-4">
                            <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.4 }}
                                className="px-4 py-1.5 bg-brand-terracotta text-white text-[10px] font-black uppercase tracking-[0.3em] rounded-full shadow-glow-sm"
                            >
                                {t('flashSale.limitedTime')}
                            </motion.div>
                            <div className="h-px w-8 bg-white/20" />
                            <span className="text-white/40 text-[10px] font-black uppercase tracking-[0.3em]">
                                {t('flashSale.grabDeals')}
                            </span>
                        </div>

                        <div className="space-y-1 pr-4">
                            <h3 className="text-4xl md:text-6xl font-black tracking-tight italic uppercase leading-[0.9]">
                                {t('flashSale.title1')} <br />
                                <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-white/40">
                                    {t('flashSale.title2')}
                                </span>
                            </h3>
                            <div className="flex items-center gap-3 pt-2">
                                <span className="text-2xl md:text-3xl font-light text-white/60 tracking-[0.2em]">{t('flashSale.unique')}</span>
                                <motion.span
                                    animate={{
                                        scale: [1, 1.1, 1],
                                        filter: ['drop-shadow(0 0 0px #FFD700)', 'drop-shadow(0 0 15px #FFD700)', 'drop-shadow(0 0 0px #FFD700)']
                                    }}
                                    transition={{ repeat: Infinity, duration: 3 }}
                                    className="text-3xl"
                                >
                                    ⚡
                                </motion.span>
                                <span className="text-5xl md:text-6xl font-black text-white leading-none tracking-tighter">{t('flashSale.upTo70')}</span>
                            </div>
                        </div>

                        {/* Premium Timer Chip */}
                        <div className="flex items-center gap-4 bg-white/5 backdrop-blur-3xl px-6 py-4 rounded-[2rem] border border-white/10 w-fit group hover:bg-white/10 transition-colors">
                            <div className="flex flex-col">
                                <span className="text-[9px] font-black uppercase tracking-widest text-white/40 mb-1">
                                    {t('flashSale.timeRemaining')}
                                </span>
                                <div className="flex gap-2 items-center text-xl font-black tabular-nums">
                                    <span className="text-brand-terracotta">{format(timeLeft.h)}</span>
                                    <span className="opacity-20">:</span>
                                    <span>{format(timeLeft.m)}</span>
                                    <span className="opacity-20">:</span>
                                    <motion.span
                                        animate={{ opacity: [1, 0.5, 1] }}
                                        transition={{ repeat: Infinity, duration: 1 }}
                                        className="text-white/60"
                                    >
                                        {format(timeLeft.s)}
                                    </motion.span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => navigate('/flash-sale')}
                        className="group relative bg-white text-black px-12 py-6 rounded-[2rem] text-sm font-black uppercase tracking-[0.2em] shadow-2xl overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-brand-terracotta/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        <span className="relative z-10 flex items-center gap-3">
                            {t('flashSale.viewDeals')}
                            <motion.span
                                animate={{ x: [0, 5, 0] }}
                                transition={{ repeat: Infinity, duration: 1.5 }}
                            >
                                →
                            </motion.span>
                        </span>
                    </motion.button>
                </div>

                {/* 🔥 Ambient Floating Icons */}
                <div className="absolute right-10 bottom-0 pointer-events-none opacity-5 md:opacity-[0.03]">
                    <motion.div
                        animate={{
                            y: [-20, 20, -20],
                            rotate: [0, 10, 0]
                        }}
                        transition={{ repeat: Infinity, duration: 8, ease: "easeInOut" }}
                        className="text-[200px]"
                    >
                        ⚡
                    </motion.div>
                </div>
            </motion.div>
        </div>
    );
};

export default FlashSalesBanner;
